import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { RouterModule } from '@angular/router';
import { NavbarComponent } from '../topNavBar/navbar.component';
import { FooterComponent } from '../shared/footer/footer.component';


@Component({
  selector: 'app-home',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatDividerModule,
    RouterModule,
    MatChipsModule,
    NavbarComponent,
    FooterComponent,
  ],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent {
  private router = inject(Router);

  // Quick service highlights shown as icon pills in the hero/services band
  // (kept short on purpose — these mirror what a frontline worker actually does day-to-day)
  quickServices = [
    { icon: 'support_agent', title: 'Remote Consultations' },
    { icon: 'psychology', title: 'AI Decision Support' },
    { icon: 'fact_check', title: 'Treatment Validation' },
    { icon: 'school', title: 'Healthcare Education' }
  ];

  // Platform features
  features = [
    {
      icon: 'support_agent',
      title: 'Frontline Worker Support',
      description: 'Equip community health workers and clinicians with simple digital tools built for every patient encounter, not just the front desk.'
    },
    {
      icon: 'psychology',
      title: 'AI Clinical Decision Support',
      description: 'Real-time, AI-assisted guidance at the point of care, helping close the gap between a patient and the right diagnosis.'
    },
    {
      icon: 'biotech',
      title: 'Laboratory Integration',
      description: 'Order tests, track specimens, and get results back into the patient record without extra paperwork.'
    },
    {
      icon: 'medication',
      title: 'Pharmacy & Medication',
      description: 'Manage prescriptions, dispensing, and stock so essential medicines reliably reach the people who need them.'
    },
    {
      icon: 'calendar_today',
      title: 'Appointments & Scheduling',
      description: 'Coordinate visits and follow-ups across facilities so no patient falls through the cracks.'
    },
    {
      icon: 'groups',
      title: 'Specialist Mentorship',
      description: 'Connect frontline teams with specialists for real-time consultations, second opinions, and mentorship — wherever they are.'
    }
  ];

  // Why e-Likita
  benefits = [
    {
      icon: 'volunteer_activism',
      title: 'Built for Frontline Workers',
      description: 'Designed around the realities of under-resourced facilities — simple, fast, and dependable when it matters most.'
    },
    {
      icon: 'psychology',
      title: 'AI-Powered, Human-Led',
      description: 'Decision support that augments clinical judgement, never replaces it. The health worker stays in control.'
    },
    {
      icon: 'security',
      title: 'FHIR Compliance',
      description: 'Built on FHIR R4 standards for safe, interoperable data exchange with the wider health system.'
    },
    {
      icon: 'cloud',
      title: 'Works Anywhere',
      description: 'Cloud-based and built with low-connectivity settings in mind, so care doesn\u2019t stop when the signal does.'
    }
  ];

  // Partners & supporters behind e-Likita
partners = [
  {
    icon: 'medical_services',
    logo: 'assets/images/afcicon.png',
    name: 'Africa Health Care Development Trust',
    url: 'https://ahdt.co.uk/',
    description: 'A network of expert healthcare specialists offering medical guidance and mentorship.'
  },
  {
    icon: 'volunteer_activism',
    logo: 'assets/images/dhtflogo.png',
    name: 'Dechi Health Trust Fund',
    url: 'https://dechihtf.org/',
    description: 'The pioneer behind this digital health initiative, dedicated to ensuring equitable access to quality healthcare and affordability in rural Nigeria.',
    featured: true
  },
  {
    icon: 'memory',
    logo: 'assets/images/dalitechicon.png',
    name: 'DAALITech',
    url: 'https://www.daalitech.com/',
    description: 'A Data and AI startup spearheading the technological development of the project.'
  }
];

  currentYear = new Date().getFullYear();

  /**
   * Navigate to hospital registration page
   */
  onRegisterHospital(): void {
    this.router.navigate(['/register-hospital']);
  }

  /**
   * Navigate to hospital selection/login page
   */
  onAccessExistingHospital(): void {
    this.router.navigate(['/login']);
  }

  onRegisterAsPatient(): void {
    // Set a flag to indicate this is a patient registration flow
    sessionStorage.setItem('registrationFlow', 'patient-self-registration');
    this.router.navigate(['/patient-hospital-selection']);
  }

  /**
   * Scroll to features section
   */
  scrollToFeatures(): void {
    const element = document.getElementById('features-section');
    element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}
